import { useState } from "react";
import { CONVERT } from "@/utils/ballisticsMath";
import { useT } from "@/i18n/translations";

const ZOOMS = [
  { id: "4x", scale: 11 },
  { id: "10x", scale: 18 },
  { id: "16x", scale: 26 },
  { id: "24x", scale: 34 },
];

const SIZE = 300;
const C = SIZE / 2;
const R = 138;

export const ReticleView = ({ metrics, clickMrad, holdoverUnit }) => {
  const t = useT();
  const [zoomId, setZoomId] = useState("10x");
  const scale = ZOOMS.find((z) => z.id === zoomId).scale;

  const dropMrad = metrics.targetDropMrad; // negative = below LOS
  const driftMrad = metrics.windDriftMrad; // positive = right

  // Impact point relative to point of aim (reticle centre).
  const clampR = R - 10;
  let ix = driftMrad * scale;
  let iy = -dropMrad * scale; // below centre => positive (down in SVG)
  const dist = Math.hypot(ix, iy);
  if (dist > clampR) {
    ix = (ix / dist) * clampR;
    iy = (iy / dist) * clampR;
  }

  const upClicks = clickMrad ? Math.round(Math.abs(dropMrad) / clickMrad) : 0;
  const windClicks = clickMrad ? Math.round(Math.abs(driftMrad) / clickMrad) : 0;
  const vDir = dropMrad < 0 ? t("dir.up") : t("dir.down");
  const hDir = driftMrad > 0 ? t("dir.leftFull") : t("dir.rightFull");

  const useMoa = holdoverUnit === "moa";
  const angVal = (mrad) =>
    useMoa
      ? `${Math.abs(mrad * CONVERT.MRAD_TO_MOA).toFixed(2)} MOA`
      : `${Math.abs(mrad).toFixed(2)} MRAD`;

  const ticks = [];
  for (let m = -8; m <= 8; m++) {
    if (m === 0) continue;
    const px = m * scale;
    if (Math.abs(px) > R - 6) continue;
    const len = m % 5 === 0 ? 8 : 4;
    ticks.push({ px, len });
  }

  return (
    <div
      className="rounded-md border border-dase-border bg-dase-panel p-4"
      data-testid="reticle-view"
    >
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-xs font-bold uppercase tracking-widest text-zinc-200">
          {t("reticle.title")}
        </h2>
        <div className="flex items-center gap-1">
          {ZOOMS.map((z) => (
            <button
              key={z.id}
              data-testid={`reticle-zoom-${z.id}`}
              onClick={() => setZoomId(z.id)}
              className={`rounded px-2 py-1 text-[10px] font-bold uppercase transition-colors ${
                zoomId === z.id
                  ? "bg-dase-red text-white"
                  : "border border-dase-border text-zinc-400 hover:text-zinc-200"
              }`}
            >
              {z.id}
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-col items-center gap-4 lg:flex-row lg:items-start">
        <svg
          viewBox={`0 0 ${SIZE} ${SIZE}`}
          className="h-64 w-64 shrink-0 rounded-full border border-dase-border bg-[#0d0f12]"
          data-testid="reticle-svg"
        >
          <defs>
            <radialGradient id="scopeVig" cx="50%" cy="50%" r="50%">
              <stop offset="70%" stopColor="#0d0f12" />
              <stop offset="100%" stopColor="#000" />
            </radialGradient>
            <filter id="glow">
              <feGaussianBlur stdDeviation="3" result="b" />
              <feMerge>
                <feMergeNode in="b" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>
          <circle cx={C} cy={C} r={R} fill="url(#scopeVig)" />
          <clipPath id="scopeClip">
            <circle cx={C} cy={C} r={R} />
          </clipPath>
          <g clipPath="url(#scopeClip)">
            {/* crosshair */}
            <line x1={C - R} y1={C} x2={C + R} y2={C} stroke="#3f3f46" strokeWidth="1" />
            <line x1={C} y1={C - R} x2={C} y2={C + R} stroke="#3f3f46" strokeWidth="1" />
            {/* hashmarks */}
            {ticks.map((t, i) => (
              <g key={i}>
                <line
                  x1={C + t.px}
                  y1={C - t.len}
                  x2={C + t.px}
                  y2={C + t.len}
                  stroke="#52525b"
                  strokeWidth="1"
                />
                <line
                  x1={C - t.len}
                  y1={C + t.px}
                  x2={C + t.len}
                  y2={C + t.px}
                  stroke="#52525b"
                  strokeWidth="1"
                />
              </g>
            ))}
            {/* impact point */}
            <line
              x1={C + ix - 7}
              y1={C + iy}
              x2={C + ix + 7}
              y2={C + iy}
              stroke="#cc0000"
              strokeWidth="1.5"
            />
            <line
              x1={C + ix}
              y1={C + iy - 7}
              x2={C + ix}
              y2={C + iy + 7}
              stroke="#cc0000"
              strokeWidth="1.5"
            />
            <circle
              cx={C + ix}
              cy={C + iy}
              r="5"
              fill="#cc0000"
              filter="url(#glow)"
              data-testid="reticle-impact-point"
            />
          </g>
        </svg>

        <div className="w-full space-y-2 font-mono text-xs">
          <div className="rounded border border-dase-border bg-dase-input p-3">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">
              {t("reticle.elevation")}
            </div>
            <div className="mt-1 text-lg font-black text-zinc-100" data-testid="reticle-elevation">
              {angVal(dropMrad)} {vDir}
            </div>
            <div className="text-dase-red">{upClicks} {t("reticle.clicks")} {vDir}</div>
          </div>
          <div className="rounded border border-dase-border bg-dase-input p-3">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">
              {t("reticle.windage")}
            </div>
            <div className="mt-1 text-lg font-black text-zinc-100" data-testid="reticle-windage">
              {angVal(driftMrad)}
            </div>
            <div className="text-dase-red">{windClicks} {t("reticle.clicks")} {hDir}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReticleView;
